"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

export type HistogramBucket = {
  min: number;
  max: number | null;
  count: number;
};

type Props = {
  buckets: HistogramBucket[] | null | undefined;
  isLoading?: boolean;
  className?: string;
  barCount: number;
  selectedMin?: number | null;
  selectedMax?: number | null;
  formatLabel?: (b: HistogramBucket) => string;
  onBucketClick?: (b: HistogramBucket) => void;
  type: "price" | "size";
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const defaultFormatLabel = (b: HistogramBucket) => `${b.min}–${b.max ?? "∞"}`;

const isOutsideRange = (
  b: HistogramBucket,
  min?: number | null,
  max?: number | null
) => {
  if (min == null && max == null) return false;

  const bMin = b.min;
  const bMax = b.max ?? Number.POSITIVE_INFINITY;

  if (min != null && bMax <= min) return true;
  if (max != null && bMin >= max) return true;
  return false;
};

const normalize = (raw: HistogramBucket[]) => {
  const maxCount = Math.max(1, ...raw.map((b) => b.count || 0));
  return raw.map((b) => ({
    ...b,
    norm: clamp01((b.count || 0) / maxCount),
  }));
};

const FilterHistogram: React.FC<Props> = ({
  buckets,
  isLoading = false,
  className,
  barCount,
  selectedMin,
  selectedMax,
  formatLabel = defaultFormatLabel,
  onBucketClick,
  type,
}) => {
  const raw = React.useMemo(
    () => (Array.isArray(buckets) ? buckets : []),
    [buckets]
  );

  const showSkeleton = raw.length === 0 && isLoading;

  const normalized = React.useMemo(() => normalize(raw), [raw]);

  return (
    <div className={twMerge("h-20 w-full", className)}>
      <div className="flex h-full w-full items-end gap-1">
        {showSkeleton
          ? Array.from({ length: barCount }).map((_, i) => (
              <div
                key={`hist-skel-${type}-${i}`}
                className="flex-1 rounded-md bg-muted/60 animate-pulse"
                style={{ height: `${20 + (i % 5) * 12}%` }}
              />
            ))
          : normalized.map((b, idx) => {
              const heightPct = Math.max(0.04, b.norm) * 100;
              const dimmed = isOutsideRange(b, selectedMin, selectedMax);
              const label = formatLabel(b);

              return (
                <button
                  key={`hist-bucket-${type}-${idx}`}
                  type="button"
                  onClick={() => onBucketClick?.(b)}
                  className={twMerge(
                    "group relative h-full flex-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    onBucketClick ? "cursor-pointer" : "cursor-default"
                  )}
                  aria-label={label}
                >
                  <div className="flex h-full w-full items-end">
                    <motion.div
                      className={twMerge(
                        "w-full rounded-md transition-colors",
                        dimmed
                          ? "bg-primary/40"
                          : "bg-primary group-hover:bg-primary/80"
                      )}
                      initial={false}
                      animate={{
                        height: `${heightPct}%`,
                        opacity: isLoading ? 0.75 : dimmed ? 0.45 : 1,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 26,
                      }}
                    />
                  </div>

                  <div className="pointer-events-none absolute -top-9 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md border bg-background px-2 py-1 text-xs shadow-sm group-hover:block">
                    <div className="font-medium">{label}</div>
                    <div className="text-muted-foreground">{b.count}</div>
                  </div>
                </button>
              );
            })}
      </div>
    </div>
  );
};

export default FilterHistogram;
